import { NgClass, NgTemplateOutlet } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, HostListener, inject, Input, OnInit } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
	ButtonComponent,
	CodeInputComponent,
	DatepickerComponent,
	FormGroupComponent,
	INavHelp,
	InputComponent,
	LinkComponent,
	NavHelpComponent,
	ProgressComponent,
	PwcsLayoutComponent,
	TileComponent,
} from '@tecalisdev/infinity';
import { ProgressService } from 'src/app/core/helpers/progress.service';
import { ReactiveFormsService } from 'src/app/core/helpers/reactive-forms.service';
import { IModalClose } from 'src/app/core/models/modal-confirmation.models';
import { NavHelpAction } from 'src/app/core/models/navHelp.models';
import { AuthService } from 'src/app/core/services/auth.service';
import { DialogConfirmationService } from 'src/app/core/services/dialog-confirmation.service';
import { LoadingService } from 'src/app/core/services/loading.service';
import { formatDateFilter } from 'src/app/core/utils/utils';
import { ModalScanQrComponent } from 'src/app/shared/components/modal-scan-qr/modal-scan-qr.component';
import { PhoneInputComponent } from 'src/app/shared/components/phone-input/phone-input.component';
import { PwcsInlineSvgComponent } from 'src/app/shared/components/pwcs-inline-svg/pwcs-inline-svg.component';
import { ModalWorkLifeHelpComponent } from 'src/app/shared/components/modal-work-life-help/modal-work-life-help.component';
import { ERROR_CODE } from 'src/app/shared/models/error.models';
import { IMaster } from 'src/app/shared/models/master.models';
import { IResultResponse } from 'src/app/shared/models/result';
import { DesignService } from 'src/app/shared/services/config/design.service';
import { MasterService } from 'src/app/shared/services/master.service';
import { LanguagePickerUiService } from 'src/app/shared/services/language-picker-ui.service';
import { NavHelpService } from 'src/app/shared/services/nav-help.service';
import { OperationService } from 'src/app/shared/services/step.service';
import { maskPhoneNumber, splitForWorkLife } from 'src/app/shared/utils/phone.util';
import { requiredPhoneE164Validator } from 'src/app/shared/validators/phone-e164.validator';
import {
	CONNECTION_TYPE,
	IConnectionOperation,
	IFinishOperation,
	IOperation,
	MODE,
	OPERATION_STATUS,
	OPERATION_TYPE,
} from 'src/app/user-identification/models/user-identification';
import { ConnectionsHelperService } from 'src/app/user-identification/services/connections/connections-helper.service';
import { ConnectionsService } from 'src/app/user-identification/services/connections/connections.service';
import { ProcessService } from 'src/app/user-identification/services/process.service';

@Component({
	selector: 'app-work-life',
	imports: [
		ReactiveFormsModule,
		TranslatePipe,
		NgClass,
		NgTemplateOutlet,
		ButtonComponent,
		FormGroupComponent,
		InputComponent,
		LinkComponent,
		NavHelpComponent,
		ProgressComponent,
		PwcsLayoutComponent,
		TileComponent,
		DatepickerComponent,
		CodeInputComponent,
		ModalScanQrComponent,
		ModalWorkLifeHelpComponent,
		PhoneInputComponent,
		PwcsInlineSvgComponent,
	],
	templateUrl: './work-life.component.html',
	styleUrls: ['./work-life.component.scss'],
})
export class WorkLifeComponent implements OnInit {
	operationService = inject(OperationService);
	private languagePickerUi = inject(LanguagePickerUiService);

	@Input()
	get operations(): IOperation[] {
		return this._operations;
	}
	set operations(operations: IOperation[]) {
		this._operations = operations;
		const chooseOperation = operations.find((op) => op.type === OPERATION_TYPE.CHOOSE);
		const workLifeOperation: any = operations.find(
			(op) => op.type === OPERATION_TYPE.CONNECTIONS && op.config?.type === CONNECTION_TYPE.WORK_LIFE,
		);
		/*
			- Si tengo la operacion choose no muestro el tutorial y realizo la
			comprobaciones por si tengo ya los datos necesarios e iniciar la operación
			- Si no tengo la operacion choose y tengo el campo que indica que se
			debe evaluar financieramiente el documento muestro el tutorial
		*/
		if (chooseOperation) {
			this.tutorial = false;
			this.initOperationIfHasOcrCompleted();
		} else {
			if (workLifeOperation?.config?.evaluation) {
				this.tutorial = true;
			} else {
				this.tutorial = false;
				this.initOperationIfHasOcrCompleted();
			}
		}
	}
	@Input() operation: IConnectionOperation;

	public OPERATION_TYPE: any = OPERATION_TYPE;
	public MODE = MODE;
	private _operations: IOperation[];
	public mode_otp: boolean = false;
	public codeOtp: string = '';
	public codeOtpError: string = '';
	public errorType: 'phone-not-found' | 'insufficient-data' | 'robot' | 'server' = null;
	public navsHelp: Array<INavHelp> = this.navHelpService.connectionsNavsHelp;
	public navsHelpTutorial: Array<INavHelp> = this.navHelpService.getConnectionsTutorialNavsHelp();
	public isVisibleScan: boolean = false;
	public isVisibleHelp: boolean = false;
	public hasOcrOperation: boolean = false;
	public form: FormGroup = new FormGroup({
		nif: new FormControl('', Validators.required),
		birthdate: new FormControl('', Validators.required),
		/** E.164 vía app-phone-input; se parte a prefix+phone al llamar a la API. */
		phone: new FormControl('', [Validators.required, requiredPhoneE164Validator()]),
		uuid_connection: new FormControl(''),
	});
	private initOperation: boolean = false;
	public tutorial: boolean = false;
	private intervalId: any;

	@HostListener('document:keydown.enter')
	onEnterKeyPress() {
		if (this.mode_otp) this.sendOtpCode();
	}

	constructor(
		public designService: DesignService,
		public progressService: ProgressService,
		public reactiveFormService: ReactiveFormsService,
		public auth: AuthService,
		private trans: TranslateService,
		private connectionsService: ConnectionsService,
		private loadingService: LoadingService,
		private navHelpService: NavHelpService,
		private dialogConfirmationService: DialogConfirmationService,
		private masterService: MasterService,
		private processService: ProcessService,
		private connectionsHelper: ConnectionsHelperService,
	) {}

	ngOnInit(): void {
		this.masterService.getMaster().subscribe((master: IMaster) => {
			if (master && !this.initOperation) {
				this.initOperation = true;
				this.processService.initOperation(this.operation.type);
			}
		});
		this.connectionsHelper.initNumberOfTries(this.operation);
	}

	// Mode enviar formulario
	public sendForm(): void {
		const phoneParts = splitForWorkLife(this.form.get('phone')?.value);
		if (this.form.valid && phoneParts) {
			this.connectionsHelper.initNumberOfTries(this.operation);
			if (this.operation?.config) this.operation.config.numberOfTries++;
			this.errorType = null;
			this.showMultipleLoading();
			this.connectionsService
				.workLifeInit({
					prefix: phoneParts.prefix,
					phone: phoneParts.phone,
					nif: this.hasOcrOperation ? undefined : this.form.get('nif').value,
					birthdate: this.hasOcrOperation ? undefined : formatDateFilter(this.form.get('birthdate').value),
					uuid_config: this.operation.uuid,
				})
				.subscribe({
					next: (response: { data: any; result: IResultResponse; uuid_connection: string }) => {
						this.mode_otp = true;
						this.form.get('uuid_connection').patchValue(response.uuid_connection);
					},
					error: (error: HttpErrorResponse) => {
						this.handleError(error);
					},
					complete: () => {
						this.loadingService.hide();
						clearInterval(this.intervalId);
					},
				});
		} else {
			this.form.markAllAsTouched();
		}
	}

	// Mode enviar OTP
	public sendOtpCode(): void {
		this.codeOtpError = '';
		this.errorType = null;
		if (this.codeOtp) {
			this.loadingService.show(this.trans.instant('GENERAL.LOADING.GET_WORK_LIFE'));
			this.connectionsService
				.workLifeOtp({
					uuid_connection: this.form.get('uuid_connection').value,
					uuid_config: this.operation.uuid,
					otp_code: this.codeOtp,
				})
				.subscribe({
					next: (response: { result: OPERATION_STATUS; process_uuid: string; process: any; data: any }) => {
						if (response.result === OPERATION_STATUS.KO) {
							this.operations.forEach((op: IOperation) => {
								if (op.type !== OPERATION_TYPE.FINISH) {
									op.completed = true;
								}
							});
						}
						const found = this.operations.find((op: IOperation) => op.type === OPERATION_TYPE.FINISH);
						if (found) {
							(found as IFinishOperation).config.status = response.result;
						}
						this.operation.completed = true;
					},
					error: (error: HttpErrorResponse) => {
						this.handleError(error);
					},
					complete: () => {
						this.loadingService.hide();
					},
				});
		} else {
			this.codeOtpError = this.trans.instant('GENERAL.VALIDATION.REQUIRED');
		}
	}

	private handleError(error: HttpErrorResponse): void {
		this.loadingService.hide();
		clearInterval(this.intervalId);

		const retry = error?.error?.retry ?? false;
		const isDefinitiveKo = error?.error?.result === OPERATION_STATUS.KO && !retry;

		if (isDefinitiveKo) {
			if (error?.error?.error?.error_code === 2028) {
				this.errorType = 'insufficient-data';
				setTimeout(() => {
					this.verificationFail(error);
				}, 5000);
			} else {
				this.errorType = 'robot';
				this.verificationFail(error);
			}
			return;
		}

		if (
			error?.error?.error?.error_code?.toString() === ERROR_CODE.FALLBACK_MANUAL &&
			error.error?.process?.length
		) {
			this.confirmManualUpload();
			return;
		}

		if (error?.error?.error?.error_code === 2028) {
			this.errorType = 'insufficient-data';
			setTimeout(() => {
				this.verificationFail(error);
			}, 5000);
		} else {
			this.errorType = 'robot';
			this.verificationFail(error);
		}
	}

	public changeToManualUpload(): void {
		const dialogMessage = {
			title: this.trans.instant('CONNECTIONS.WORK_LIFE.MANUAL.TITLE'),
			description: this.trans.instant('CONNECTIONS.WORK_LIFE.MANUAL.DESCRIPTION'),
			buttonPrimaryLabel: this.trans.instant('GENERAL.BUTTON.YES_ATTACH'),
			buttonSecondaryLabel: this.trans.instant('GENERAL.BUTTON.CANCEL'),
		};
		this.dialogConfirmationService.show(dialogMessage).subscribe((event: IModalClose) => {
			if (event === 'buttonPrimary') {
				this.confirmManualUpload();
			}
			this.dialogConfirmationService.hide();
		});
	}

	private confirmManualUpload(): void {
		this.loadingService.show(this.trans.instant('GENERAL.LOADING.CHANGE_TO_MANUAL'));
		this.operation.completed = true;
		const positionOperation = this.operations.findIndex((op: IOperation) => op.uuid === this.operation.uuid);
		if (positionOperation !== -1) {
			this.operations.splice(
				positionOperation,
				0,
				this.connectionsHelper.getConnectionDocType(CONNECTION_TYPE.WORK_LIFE, this.operation),
			);
			this.operationService.setOperations(this.operations);
		}
	}

	private showMultipleLoading(): void {
		let minute = 0;
		const loadingMessages = [
			this.trans.instant('CONNECTIONS.WORK_LIFE.CONNECTED_WITH_SS'),
			this.trans.instant('CONNECTIONS.WORK_LIFE.GET_CODE'),
		];

		this.loadingService.show(loadingMessages[0]);
		this.intervalId = setInterval(() => {
			minute++;
			const msg = loadingMessages[minute] || loadingMessages[loadingMessages.length - 1];
			this.loadingService.show(msg);
		}, 60000); // Cada minuto
	}

	get subtitle(): string {
		if (this.tutorial) {
			return this.trans.instant('CONNECTIONS.WORK_LIFE.TUTORIAL_TITLE');
		}
		if (this.mode_otp) {
			const phoneParts = splitForWorkLife(this.form.get('phone')?.value);
			const maskedPhone = phoneParts
				? `${phoneParts.prefix} ${maskPhoneNumber(phoneParts.phone)}`
				: '';
			return this.trans.instant('CONNECTIONS.WORK_LIFE.TITLE_OTP', {
				phone: maskedPhone,
			});
		}
		if (!this.mode_otp) {
			return this.trans.instant('CONNECTIONS.WORK_LIFE.TITLE');
		}
		return '';
	}

	private verificationFail(error: HttpErrorResponse): void {
		const retry = error?.error?.retry ?? false;
		if (error?.error?.result === OPERATION_STATUS.KO && !retry) {
			this.operation.completed = true;
			this.operations.forEach((op: IOperation) => {
				if (op.type !== OPERATION_TYPE.FINISH) {
					op.completed = true;
				}
			});
			const foundFinish = this.operations.find((op: IOperation) => op.type === OPERATION_TYPE.FINISH);
			if (foundFinish) {
				(foundFinish as IFinishOperation).config.status = error?.error?.result;
			}
		}
	}

	private initOperationIfHasOcrCompleted(): void {
		// if (this.operations.find((op) => op.type === OPERATION_TYPE.OCR)?.completed) {
		this.hasOcrOperation = true;
		this.form.get('nif').setValidators(null);
		this.form.get('nif').updateValueAndValidity({ emitEvent: false });
		this.form.get('birthdate').setValidators(null);
		this.form.get('birthdate').updateValueAndValidity({ emitEvent: false });
		// }
	}

	public finishTutorial(): void {
		this.tutorial = false;
		this.initOperationIfHasOcrCompleted();
	}

	/*
	 * Nav Help
	 */
	public navHelpChange(action: INavHelp): void {
		const actions = {
			[NavHelpAction.QR]: () => (this.isVisibleScan = true),
			[NavHelpAction.LANGUAGE]: () => this.languagePickerUi.open(),
			[NavHelpAction.HELP]: () => (this.isVisibleHelp = true),
			// [NavHelpAction.VOLUMEN]: () => this.navHelpService.toggleAudioGuide(action),
		};
		return actions[action.id]();
	}
}
